import React, { useState, useRef, useCallback, useEffect } from 'react';
import { GoogleMap, useLoadScript, Marker } from '@react-google-maps/api';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/header/Header';
import Footer from '../../components/footer/Footer';
import BenefitsSection from './components/BenefitsSection';
import './Clubs.css';
import LoginModal from '../../components/loginSection/LoginModal';
import { clubsService } from '../../services';
import { getClubPublicPath } from '../../utils/clubPublicPaths';

const libraries = ['places'];

const mapContainerStyle = {
    width: '100%',
    height: '100%',
    borderRadius: '16px'
};

const mapOptions = {
    disableDefaultUI: false,
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true,
    styles: [
        {
            featureType: "all",
            elementType: "geometry",
            stylers: [{ color: "#f5f5f5" }]
        },
        {
            featureType: "water",
            elementType: "geometry",
            stylers: [{ color: "#e9e9e9" }]
        },
        {
            featureType: "water",
            elementType: "labels.text.fill",
            stylers: [{ color: "#9e9e9e" }]
        }
    ]
};

const Clubs = () => {
    const navigate = useNavigate();
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [clubs, setClubs] = useState([]);
    const [location, setLocation] = useState('');
    const [clubName, setClubName] = useState('');
    const [discipline, setDiscipline] = useState('');
    const [wadPolicy, setWadPolicy] = useState('');
    const [maxPricePerClay, setMaxPricePerClay] = useState('');
    const [minRifleDistance, setMinRifleDistance] = useState('');
    const [hasElectronicTargets, setHasElectronicTargets] = useState(false);
    const [requiresNonToxicShot, setRequiresNonToxicShot] = useState(false);
    const [dogsAllowed, setDogsAllowed] = useState(false);
    const [center, setCenter] = useState({ lat: 54.5, lng: -4 });
    const [zoom, setZoom] = useState(5);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedClub, setSelectedClub] = useState(null);
    const [selectedClubDetails, setSelectedClubDetails] = useState(null);
    const [isLoadingSelectedClubDetails, setIsLoadingSelectedClubDetails] = useState(false);
    const [error, setError] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [clubStats, setClubStats] = useState(null);
    const mapRef = useRef(null);

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: "AIzaSyDu74EMNNqnvTVsAoz-375ia0BLH0A3HkU",
        libraries
    });

    const openLoginModal = () => setIsLoginModalOpen(true);
    const closeLoginModal = () => setIsLoginModalOpen(false);

    const fetchClubs = async () => {
        if (!location && !clubName) {
            setError("Please enter a location or club name");
            return;
        }

        setIsLoading(true);
        setError(null);
        setHasSearched(true);

        try {
            const params = new URLSearchParams();
            if (location) params.append('location', location);
            if (clubName) params.append('clubName', clubName);
            if (discipline) params.append('discipline', discipline);
            if (wadPolicy) params.append('wadPolicy', wadPolicy);
            if (maxPricePerClay) params.append('maxPricePerClay', maxPricePerClay);
            if (minRifleDistance) params.append('minRifleDistance', minRifleDistance);
            if (hasElectronicTargets) params.append('hasElectronicTargets', 'true');
            if (requiresNonToxicShot) params.append('requiresNonToxicShot', 'true');
            if (dogsAllowed) params.append('dogsAllowed', 'true');

            const data = await clubsService.advancedSearch(params, { credentials: 'include' });
            setClubs(data);

            if (data.length > 0) {
                setCenter({ lat: data[0].latitude, lng: data[0].longitude });
                setZoom(10);
                setSelectedClub(data[0]);
            } else {
                setError("No clubs found matching your search criteria");
            }
        } catch (error) {
            console.error('Error fetching clubs:', error);
            setError(`Failed to search for clubs: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchClubs();
    };

    const onMapLoad = useCallback((map) => {
        mapRef.current = map;
    }, []);

    const handleClubSelect = useCallback((club) => {
        setSelectedClub(club);
        if (club) {
            setCenter({ lat: club.latitude, lng: club.longitude });
            setZoom(14);
        }
    }, []);

    useEffect(() => {
        const loadClubStats = async () => {
            try {
                const stats = await clubsService.getClubStats();
                setClubStats(stats);
            } catch (statsError) {
                console.error('Error fetching club stats:', statsError);
            }
        };

        loadClubStats();
    }, []);

    useEffect(() => {
        const loadSelectedClubDetails = async () => {
            if (!selectedClub?.clubId) {
                setSelectedClubDetails(null);
                return;
            }

            setIsLoadingSelectedClubDetails(true);
            try {
                const clubDetails = await clubsService.getPublicClubProfile(selectedClub.clubId);
                setSelectedClubDetails(clubDetails);
            } catch (error) {
                console.error('Error fetching selected club details:', error);
                setSelectedClubDetails(null);
            } finally {
                setIsLoadingSelectedClubDetails(false);
            }
        };

        loadSelectedClubDetails();
    }, [selectedClub?.clubId]);

    const handleViewProfile = useCallback((event, club) => {
        event.stopPropagation();
        navigate(getClubPublicPath(club));
    }, [navigate]);

    return (
        <div className="clubs-page">
            <Header openLoginModal={openLoginModal} />
            
            {/* Hero Section */}
            <section className="clubs-hero">
                <div className="clubs-hero-bg"></div>
                <div className="container">
                    <div className="clubs-hero-content">
                        <span className="clubs-hero-badge">Find Your Venue</span>
                        <h1 className="clubs-hero-title">
                            Discover Clubs, Ranges<br />
                            <span>& Shoots Near You</span>
                        </h1>
                        <p className="clubs-hero-subtitle">
                            Search our extensive database of shooting venues across the UK - 
                            clay grounds, rifle ranges, game estates, and syndicate pegs.
                        </p>
                        {clubStats && (
                            <div className="clubs-hero-stats" style={{ marginTop: '18px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <span className="clubs-hero-badge">
                                    {clubStats.totalVenues} listed venues
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Search Section */}
            <section className="clubs-search-section">
                <div className="container">
                    <div className="clubs-search-card">
                        <form className="clubs-search-form" onSubmit={handleSearch}>
                            <div className="search-input-group">
                                <div className="search-input-wrapper">
                                    <input
                                        type="text"
                                        id="location"
                                        name="location"
                                        placeholder="Enter postcode or location"
                                        className="search-input"
                                        value={location}
                                        onChange={(e) => setLocation(e.target.value)}
                                        aria-label="Search by location"
                                    />
                                </div>
                                <div className="search-input-wrapper">
                                    <input
                                        type="text"
                                        id="clubName"
                                        name="clubName"
                                        placeholder="Enter venue name"
                                        className="search-input"
                                        value={clubName}
                                        onChange={(e) => setClubName(e.target.value)}
                                        aria-label="Search by club name"
                                    />
                                </div>
                                <button type="submit" className="search-btn" disabled={isLoading}>
                                    {isLoading ? (
                                        <span className="search-btn-loading">
                                            <svg className="spinner" viewBox="0 0 24 24">
                                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="31.4 31.4" />
                                            </svg>
                                            Searching...
                                        </span>
                                    ) : (
                                        <>
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <circle cx="11" cy="11" r="8"></circle>
                                                <path d="m21 21-4.35-4.35"></path>
                                            </svg>
                                            Search Venues
                                        </>
                                    )}
                                </button>
                            </div>
                            <div className="search-input-group" style={{ marginTop: '10px', flexWrap: 'wrap' }}>
                                <div className="search-input-wrapper" style={{ minWidth: '170px' }}>
                                    <select className="search-input" value={discipline} onChange={(e) => setDiscipline(e.target.value)}>
                                        <option value="">All disciplines</option>
                                        <option value="Clay">Clay</option>
                                        <option value="Rifle">Rifle</option>
                                        <option value="Game">Game</option>
                                        <option value="Mixed">Mixed</option>
                                    </select>
                                </div>
                                <div className="search-input-wrapper" style={{ minWidth: '170px' }}>
                                    <select className="search-input" value={wadPolicy} onChange={(e) => setWadPolicy(e.target.value)}>
                                        <option value="">Any wad policy</option>
                                        <option value="FibreOnly">Fibre only</option>
                                        <option value="PlasticOnly">Plastic only</option>
                                        <option value="Both">Both</option>
                                    </select>
                                </div>
                                <div className="search-input-wrapper" style={{ minWidth: '170px' }}>
                                    <input
                                        type="number"
                                        className="search-input"
                                        placeholder="Max price per clay"
                                        value={maxPricePerClay}
                                        onChange={(e) => setMaxPricePerClay(e.target.value)}
                                    />
                                </div>
                                <div className="search-input-wrapper" style={{ minWidth: '170px' }}>
                                    <input
                                        type="number"
                                        className="search-input"
                                        placeholder="Min rifle distance"
                                        value={minRifleDistance}
                                        onChange={(e) => setMinRifleDistance(e.target.value)}
                                    />
                                </div>
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                                    <input type="checkbox" checked={hasElectronicTargets} onChange={(e) => setHasElectronicTargets(e.target.checked)} />
                                    Electronic targets
                                </label>
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                                    <input type="checkbox" checked={requiresNonToxicShot} onChange={(e) => setRequiresNonToxicShot(e.target.checked)} />
                                    Non-toxic shot required
                                </label>
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                                    <input type="checkbox" checked={dogsAllowed} onChange={(e) => setDogsAllowed(e.target.checked)} />
                                    Dogs allowed
                                </label>
                            </div>
                        </form>

                        {error && (
                            <div className="search-error">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="8" x2="12" y2="12"></line>
                                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                </svg>
                                {error}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Results Section */}
            <section className="clubs-results-section">
                <div className="container">
                    <div className="clubs-results-grid">
                        {/* Map */}
                        <div className="clubs-map-container">
                            <div className="clubs-map">
                                {isLoaded ? (
                                    <GoogleMap
                                        mapContainerStyle={mapContainerStyle}
                                        center={center}
                                        zoom={zoom}
                                        onLoad={onMapLoad}
                                        options={mapOptions}
                                    >
                                        {clubs.map((club) => (
                                            <Marker
                                                key={club.clubId}
                                                position={{ lat: club.latitude, lng: club.longitude }}
                                                title={club.name}
                                                onClick={() => handleClubSelect(club)}
                                                animation={selectedClub?.clubId === club.clubId ?
                                                    window.google.maps.Animation.BOUNCE : null}
                                            />
                                        ))}
                                    </GoogleMap>
                                ) : (
                                    <div className="map-loading">
                                        <svg className="spinner" viewBox="0 0 24 24">
                                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="31.4 31.4" />
                                        </svg>
                                        Loading map...
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Club List */}
                        <div className="clubs-list-container">
                            <div className="clubs-list-header">
                                <h3>
                                    {hasSearched ? (
                                        clubs.length > 0 ? `${clubs.length} Clubs Found` : 'No Results'
                                    ) : 'Club Results'}
                                </h3>
                                {clubs.length > 0 && (
                                    <span className="clubs-list-count">{clubs.length} results</span>
                                )}
                            </div>

                            <div className="clubs-list">
                                {clubs.length > 0 ? (
                                    clubs.map((club) => (
                                        <div
                                            key={club.clubId}
                                            className={`club-card ${selectedClub?.clubId === club.clubId ? 'selected' : ''}`}
                                            onClick={() => handleClubSelect(club)}
                                        >
                                            <div className="club-card-header">
                                                <h4 className="club-card-name">{club.name}</h4>
                                                {club.distance && (
                                                    <span className="club-card-distance">
                                                        {club.distance.toFixed(1)} km
                                                    </span>
                                                )}
                                            </div>
                                            <p className="club-card-address">{club.address}</p>
                                            {selectedClub?.clubId === club.clubId && (
                                                <div className="club-card-details">
                                                    {club.phoneNumber && (
                                                        <div className="club-detail">
                                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                                            </svg>
                                                            <span>{club.phoneNumber}</span>
                                                        </div>
                                                    )}
                                                    {club.email && (
                                                        <div className="club-detail">
                                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                                                <polyline points="22,6 12,13 2,6"></polyline>
                                                            </svg>
                                                            <span>{club.email}</span>
                                                        </div>
                                                    )}
                                                    {club.website && (
                                                        <a 
                                                            href={club.website.startsWith('http') ? club.website : `https://${club.website}`}
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="club-detail club-website"
                                                        >
                                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <circle cx="12" cy="12" r="10"></circle>
                                                                <line x1="2" y1="12" x2="22" y2="12"></line>
                                                                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                                                            </svg>
                                                            <span>Visit Website</span>
                                                        </a>
                                                    )}
                                                    {isLoadingSelectedClubDetails && selectedClub?.clubId === club.clubId && (
                                                        <div className="club-offer-preview loading">Loading venue perks...</div>
                                                    )}
                                                    {!isLoadingSelectedClubDetails &&
                                                        selectedClub?.clubId === club.clubId &&
                                                        selectedClubDetails?.offers?.length > 0 && (
                                                            <div className="club-offer-preview">
                                                                <span className="club-offer-preview-badge">
                                                                    {selectedClubDetails.offers.length} live offer{selectedClubDetails.offers.length !== 1 ? 's' : ''}
                                                                </span>
                                                                <p>{selectedClubDetails.offers[0].title}</p>
                                                            </div>
                                                        )}
                                                    <button
                                                        type="button"
                                                        className="club-view-profile-btn"
                                                        onClick={(event) => handleViewProfile(event, selectedClubDetails || club)}
                                                    >
                                                        View full profile
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div className="clubs-list-empty">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                            <circle cx="12" cy="10" r="3"></circle>
                                        </svg>
                                        <h4>Search for venues</h4>
                                        <p>Enter a location or venue name to find clubs, ranges, and shoots near you.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <BenefitsSection />
            
            <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} />
            <Footer />
        </div>
    );
};

export default Clubs;
